import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { ChevronDown } from 'lucide-react'
import { emit } from '../../platform/analytics/analytics'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { INVITABLE_ROLES, isLastOwner, ROLE_LABELS, type Role } from './roles'
import { describeRoleFailure } from './rowFailure'
import { useRowMutation } from './useRowMutation'

/**
 * The role-change control a row's trailing slot shows in place of the plain
 * role text — Task 5. `MemberRow` renders this only once it has already
 * confirmed `canChangeRole(myRole)` and `!isSelf(...)`, so everything below
 * assumes the viewer IS allowed to open this menu; the one guard left for
 * this component to enforce itself is the last-owner one, because it is
 * PER-OPTION rather than per-row — the control stays present, and only the
 * options that would leave the household without an owner are disabled.
 *
 * **The last-owner branch is defensive, and cannot render on the mounted
 * screen today.** `Household.tsx` feeds both `myRole` and this component's
 * own `members` from the same array, so whenever the control renders at all
 * the viewer is a distinct `owner` row in it — which makes the target row's
 * "am I the sole owner" count at least two and `blocked` always `false`.
 * `INVITABLE_ROLES` also excludes `owner`, so no product path creates a
 * second owner in the first place. The guard stays anyway (cheap, and
 * correct defence in depth) because it goes LIVE once Tasks 6 and 7 let an
 * admin remove an owner or an owner leave — at which point a household can
 * genuinely reach one owner while this menu is still open on them
 * (web-architect review, issue 312, non-blocking finding 1).
 *
 * **Built by hand rather than through a shared primitive.** `CategoryTable`'s
 * own overflow-menu note records that this repo has no popup-menu primitive
 * yet, and one item is not what earns it. This is the FIRST `role="menu"` in
 * `src/`, not the second as an earlier draft of this comment said —
 * `CategoryTable.tsx:229-241` considered one and declined, and there is no
 * other in `src/` (web-architect review, issue 312, finding N7). Task 6 added
 * a second, `MemberActionsMenu` — still not extracted, since two call sites
 * is still under the bar this repo's own three-progress-rings lesson sets;
 * see that file's own docblock for why it stayed a second hand-rolled copy
 * rather than a shared one.
 *
 * **Task 13 adds a second FORM, not a second copy of the logic.** M12-R20 —
 * "role changes use a bottom-sheet picker rather than an inline dropdown" on
 * phone — is answered by mounting `Overlay` (`placement="bottom"`) beside the
 * hand-rolled panel below rather than replacing it: `useBreakpoint()` picks
 * exactly one of the two at render time, and both read the identical
 * `options`/`blocked`/`choose` this docblock already describes. See
 * `renderOptions`'s own docblock for the two things that DO differ between
 * them (ARIA role, and whether arrow-key roving is wired at all) and why.
 */

type RoleMenuProps = {
  /** The member's role, already narrowed — the caller (`MemberRow`) renders
   *  this component only after confirming `member.role !== null`. */
  role: Role
  /** The same member's role as the server actually sent it, for `isLastOwner`
   *  — which takes the wire value, not the narrowed one (`roles.ts`'s own
   *  note on why `rawRole` exists). */
  rawRole: string
  /** The member's display name or email, for the trigger's accessible name
   *  and the reason text. Resolved once by `MemberRow`, which already carries
   *  the display-name/email fallback for the row's own label. */
  label: string
  /** The full roster, `rawRole` only — what `isLastOwner` needs to decide
   *  whether `rawRole` is the household's only owner. */
  members: readonly { rawRole: string }[]
  /**
   * `Household.tsx` passes `useChangeRole`'s own `change` here directly — the
   * two signatures match once `MemberRow` has bound the membership id. A
   * promise, not `void`: `InvitationRow`'s own resend/revoke (Task 10) is the
   * precedent for surfacing a failed mutation per-ROW rather than letting it
   * vanish, and there is no failure to surface without one.
   */
  onChangeRole: (role: Role) => Promise<void>
}

export function RoleMenu({ role, rawRole, label, members, onChangeRole }: RoleMenuProps) {
  const [open, setOpen] = useState(false)
  // M12-R20: a bottom-sheet picker on phone rather than the anchored dropdown
  // below — `Overlay`'s own `placement="bottom"` docblock explains why the
  // breakpoint switch belongs HERE rather than inside `Overlay` the way
  // `placement="sheet"` does its own.
  const phone = useBreakpoint() === 'phone'
  /**
   * Per-ROW state, deliberately not `useChangeRole`'s shared `isPending` —
   * `useRowMutation`'s own docblock states the arity argument once
   * (`InvitationRow`'s `resending` is the sibling copy this repo used to
   * carry twice): the hook is called once for the whole list in
   * `Household.tsx`, so its `isPending` would disable every row's menu for
   * one row's change in flight.
   */
  const changeMutation = useRowMutation(
    onChangeRole,
    (cause) => describeRoleFailure(label, cause),
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Unique per mounted menu, so `aria-describedby` below points at THIS row's
  // reason and not some other row's — several `RoleMenu`s can be in the DOM
  // (closed) at once, one per member.
  const reasonId = useId()
  // Ditto, for the trigger's own hidden role description — see its render.
  const roleDescId = useId()

  // The three assignable roles, plus the current one when it is not already
  // among them — `owner` is never invitable (`roles.ts`), so a member who IS
  // one only sees it here as "where they currently stand", never as
  // something this menu can assign TO someone else.
  const options: Role[] = INVITABLE_ROLES.includes(role) ? [...INVITABLE_ROLES] : [role, ...INVITABLE_ROLES]

  // True only when `rawRole` is the household's sole owner (`isLastOwner`
  // returns `false` immediately for every other role) — the one condition
  // that must disable every OTHER option here, before the server has to say
  // "Cannot demote the last owner".
  const blocked = isLastOwner(
    members.map((m) => ({ role: m.rawRole })),
    { role: rawRole },
  )

  useEffect(() => {
    // Not on phone: `Overlay` (`placement="bottom"`) owns Escape and
    // outside-click itself once the picker is a portal-rendered dialog — see
    // its own `onKeyDown`/scrim-click. Reaching this document-level pair
    // there would misfire on every click INSIDE the sheet too, since the
    // portal renders it outside `rootRef` (this component's own trigger and
    // its containing block, not the sheet's), which is exactly what "outside
    // `rootRef`" is testing for.
    if (!open || phone) return

    // Outside click and Escape both close the menu without acting — neither
    // is exercised by `MemberRow.role.test.tsx`, but CLAUDE.md's a11y bar
    // asks for "a real keyboard path", and a menu with no way out but
    // selecting an option is not one.
    function onDocumentPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    function onDocumentKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onDocumentPointerDown)
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentPointerDown)
      document.removeEventListener('keydown', onDocumentKeyDown)
    }
  }, [open, phone])

  useEffect(() => {
    // Not on phone: `Overlay`'s own `useFocusTrap` already focuses the first
    // focusable element the moment the sheet mounts — the identical DOM node
    // this effect would otherwise focus a second time, since the item
    // buttons below are shared between both forms.
    if (phone) return

    // ARIA's menu-button pattern opens onto its first item, not onto nothing
    // — a mouse click lands the pointer wherever it clicked, but a keyboard
    // or screen-reader user needs the menu itself to move focus in.
    //
    // This is a no-op in a browser when item 0 is `disabled` (verified in
    // Chromium: `focus()` on a disabled button leaves `activeElement`
    // untouched) — true for every `admin`/`owner` row, since item 0 is
    // whichever option is first and the current role is often it
    // (web-architect review, issue 312, blocking finding 1). jsdom does not
    // enforce this, which is why the unit tests below stayed green with focus
    // never entering the menu. Items below no longer use native `disabled`
    // for exactly this reason — see the item render and `choose`.
    if (open) itemRefs.current[0]?.focus()
  }, [open, phone])

  function moveFocus(from: number, delta: number) {
    const count = itemRefs.current.length
    if (count === 0) return
    // Wraps in both directions — Up from the first item reaches the last and
    // Down from the last reaches the first, the standard ARIA menu behaviour.
    const next = (from + delta + count) % count
    itemRefs.current[next]?.focus()
  }

  function onItemKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveFocus(index, 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveFocus(index, -1)
    }
  }

  function choose(option: Role) {
    // Items are `aria-disabled`, not `disabled` (see the item render below),
    // so a blocked option still fires a click — this is the guard native
    // `disabled` used to give for free.
    if (blocked && option !== role) return
    // Unreachable by construction while the trigger's own guard (below)
    // holds: `open` and `changeMutation.pending` flip together in THIS same
    // function, so no item can ever be clicked while a change is in flight —
    // reopening is what would be needed first, and the trigger's own `if
    // (changeMutation.pending) return` refuses that. A backstop for the
    // trigger's guard ALSO being broken, not a race against an
    // already-queued click — an earlier draft of this comment claimed one;
    // `MemberRow.role.test.tsx`'s own investigation (three different ways
    // tried to reach this line, none did) found there is none to guard
    // against.
    if (changeMutation.pending) return
    setOpen(false)
    triggerRef.current?.focus()
    if (option === role) return
    // Task 12. `member_role_changed` only once the PATCH actually resolves —
    // a rejected change never happened, and the row's own `changeMutation.
    // failure` already surfaces that. `role` and `option` are both narrowed
    // `Role` values (never `rawRole`), so this can only ever carry one of the
    // four enum members `vocabulary.ts` allows through `role`/`from_role`/
    // `to_role` — never a name.
    void changeMutation
      .run(option)
      .then(() => emit('member_role_changed', { from_role: role, to_role: option }))
      .catch(() => {})
  }

  /**
   * Shared between both forms below — the anchored dropdown (tablet and up)
   * and the phone bottom sheet — because the CHOICE is identical between
   * them; only the container differs. `menuSemantics` is false for the sheet:
   * it is a modal `Overlay` (`role="dialog"`), and an ARIA `menu` inside a
   * `dialog` — with `useFocusTrap`'s own plain Tab order competing with the
   * roving-focus `onItemKeyDown` gives a real `menu` — is two navigation
   * models answering the same click, not one. The dropdown carries the ARIA
   * menu pattern; the sheet's items are plain buttons.
   *
   * **The dropdown's items are `menuitemradio`, not `menuitem` (issue 369).**
   * They are a single-select set and that is the role for one. An earlier
   * round deliberately shipped `menuitem` because
   * `e2e/specs/household-role-changes.spec.ts` queried it and CLAUDE.md's
   * hard stop meant an implementer could not edit the tester's file — a
   * rename would have broken three assertions silently, with no change to
   * what they test. Issue 369 is that debt, carded so one commit could move
   * both sides at once. Seven unit assertions moved with them.
   *
   * `menuSemantics` also gates `onItemKeyDown` below (web-architect review,
   * issue 320, finding 2) — an earlier draft wired arrow-key roving
   * unconditionally, which is exactly the second navigation model this
   * paragraph argues against admitting into the dialog. Inside the sheet,
   * `useFocusTrap`'s own Tab order is the only navigation this menu offers;
   * arrow keys there do nothing, same as Tab does nothing in the anchored
   * `role="menu"` today.
   */
  function renderOptions(menuSemantics: boolean) {
    return options.map((option, index) => {
      // `disabled={option === role || blocked}` used to conflate "this is
      // what they already are" with "this is not allowed" (N3). The fix is
      // not marking the current-role item as disabled/blocked at all —
      // clicking it is already a harmless no-op (`choose`, above) — so only
      // an option that is BOTH blocked and not the current one is ever
      // `aria-disabled`.
      const itemBlocked = blocked && option !== role
      return (
        <Button
          key={option}
          ref={(el) => {
            itemRefs.current[index] = el
          }}
          // `menuitemradio`, not `menuitem` (issue 369): the options are a
          // single-select set — exactly one of them is the member's current
          // role — and `menuitem` handed a screen-reader user four
          // undifferentiated items with no indication which was in effect.
          //
          // `MemberActionsMenu`'s items stay `menuitem` and are NOT the same
          // case: Remove and Leave are actions, not a selection set.
          role={menuSemantics ? 'menuitemradio' : undefined}
          // Only meaningful alongside the role above, so it is scoped to the
          // same branch — an `aria-checked` on the sheet's plain buttons
          // would be a state expression on an element with no state to hold.
          aria-checked={menuSemantics ? option === role : undefined}
          // `aria-disabled`, not `disabled` — a native `disabled` item cannot
          // receive focus, which is what left the menu unreachable by
          // keyboard for an `admin`/`owner` row (blocking finding 1: item 0
          // is disabled exactly when the member's current role sorts
          // first). `choose` above carries the guard `disabled` used to give
          // for free.
          aria-disabled={itemBlocked || undefined}
          aria-describedby={itemBlocked ? reasonId : undefined}
          variant="chip"
          size="panel"
          className="role-menu__item"
          // Only the dropdown gets arrow-key roving — see this function's own
          // docblock. `undefined`, not a no-op function, so the sheet's items
          // carry no keydown handler at all rather than one that does nothing.
          onKeyDown={menuSemantics ? (event) => onItemKeyDown(event, index) : undefined}
          onClick={() => choose(option)}
        >
          {ROLE_LABELS[option]}
        </Button>
      )
    })
  }

  const reason = blocked && (
    <p id={reasonId} className="role-menu__reason">
      {label} is the household&rsquo;s last owner — make someone else an owner first.
    </p>
  )

  return (
    <div className="role-menu" ref={rootRef}>
      <Button
        ref={triggerRef}
        variant="chip"
        size="panel"
        className="role-menu__trigger"
        // `'dialog'` on phone — the trigger opens `Overlay`'s bottom sheet
        // there, never the `role="menu"` dropdown a desktop press opens.
        aria-haspopup={phone ? 'dialog' : 'menu'}
        aria-expanded={open}
        aria-label={`Change ${label}'s role`}
        // `aria-label` REPLACES the button's own content in its accessible
        // NAME, so the visible role text ("Member", "Admin", …) — the one
        // thing an owner can act on this row and the one thing a
        // screen-reader user would otherwise learn from it, same as every
        // OTHER row's plain-text role — was never announced (web-architect
        // review, issue 312, re-gate finding). `aria-describedby` instead of
        // widening the label: `e2e/specs/household-role-changes.spec.ts`
        // (untracked, the tester's) matches the NAME as a full string, and a
        // description leaves it untouched.
        aria-describedby={roleDescId}
        aria-busy={changeMutation.pending}
        // NOT `disabled={changeMutation.pending}` — a disabled focused element
        // drops focus to `<body>` in Chromium with nothing to restore it
        // (verified; web-architect review, issue 312, blocking finding 2).
        // `aria-busy` already says the control is working; the guard below
        // gives the same double-submit protection `InvitationRow`'s
        // `resending` flag gives its own (enabled) button.
        onClick={() => {
          if (changeMutation.pending) return
          setOpen((o) => !o)
        }}
      >
        {changeMutation.pending ? 'Changing…' : ROLE_LABELS[role]}
        <Icon glyph={ChevronDown} />
        {/* `.sr-only` (`reset.css`) — not a second copy of the shape. Read by
            `aria-describedby` above; visually redundant with the trigger's
            own text, which is exactly the point: sighted and non-visual
            users learn the same fact, just through different channels. */}
        <span id={roleDescId} className="sr-only">
          Currently {ROLE_LABELS[role]}
        </span>
      </Button>
      {/* Each item is `role="menuitemradio"` with `aria-checked` — the ARIA
          menu pattern's own answer for "which option is the current one".
          Issue 369, and it was deferred once rather than skipped: the earlier
          round could not edit `e2e/specs/household-role-changes.spec.ts`
          (the tester's), so a rename would have broken three assertions
          silently with no change to what they test. The card exists to hold
          both sides at once, and did — four e2e queries, nine unit
          assertions, and this render all moved in one commit.

          The card named three e2e queries and seven unit ones; the real
          counts were found by grepping `src/` and `e2e/` for the role rather
          than by trusting the enumeration. `role-menu-bottom-sheet.mobile
          .spec.ts` was the one a narrower search missed, and it failed in
          `make verify` rather than before it. */}
      {open && !phone && (
        <div className="role-menu__panel" role="menu" aria-label={`${label}'s role`}>
          {renderOptions(true)}
          {reason}
        </div>
      )}
      {/* M12-R20 — the bottom-sheet form. `Overlay` owns its own portal,
          scrim, focus trap, Escape and outside-click; this component supplies
          only `open` and the content, exactly as `InviteDialog` does for its
          own `placement="sheet"` (M12-R19). Only ever mounted on phone: see
          the prop's own docblock for why the switch lives at this call site
          rather than inside `Overlay`. */}
      {phone && (
        <Overlay
          open={open}
          onClose={() => setOpen(false)}
          label={`Change ${label}'s role`}
          placement="bottom"
          restoreFocusTo={triggerRef}
        >
          <div className="role-menu__sheet">
            {/* `<h2>`, matching `Dialog.tsx`'s own `.dialog__title` — a
                visible echo of the `label` above, same duplication that
                component's `ConfirmDialog` and its plain form carry for the
                identical reason: the accessible name and the sighted title
                are one fact, stated twice through two channels.

                The trailing `sr-only` span is the fix for a phone-only
                regression (web-architect review, issue 320, finding 1):
                `Overlay`'s modal form makes the whole rest of `document.body`
                — including this component's own trigger and the "Currently
                {ROLE_LABELS[role]}" span inside it — `inert`, so a
                screen-reader user with the sheet open had nothing reachable
                telling them which role {label} already holds. The heading is
                the one node in this sheet guaranteed to exist and be exposed
                regardless of which items render, so the fact lives here
                rather than on a specific item — `renderOptions`'s own
                docblock is why `aria-current`/`menuitemradio` on an item is
                deliberately NOT how (that shape is issue 369, deferred). The
                visible title is untouched; only a non-visual reader gets the
                extra clause. */}
            <h2 className="role-menu__sheet-title">
              {`Change ${label}'s role`}
              <span className="sr-only">{` — currently ${ROLE_LABELS[role]}`}</span>
            </h2>
            {renderOptions(false)}
            {reason}
          </div>
        </Overlay>
      )}
      {changeMutation.failure !== null && (
        <p className="role-menu__failure" role="alert">
          {changeMutation.failure}
        </p>
      )}
    </div>
  )
}
